import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import { IBaseStatusDto } from "src/types";
import { Box, Grid, MenuItem, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { API_URLS, ResDataType } from "src/api/apiUrls";
import useApiServices from "src/api/useApiServices";
import { Block } from "src/components/Block";
import AppButton from "src/components/common/AppButton";
import AppLoadingButton from "src/components/common/AppLoadingButton";
import { ConfirmDialog } from "src/components/custom-dialog";
import FormProvider, {
  RHFMultiSelect,
  RHFSelect,
  RHFTextField,
} from "src/components/hook-form";
import { useBoolean } from "src/hooks/use-boolean";
import AppLoadingAndErrorWrapper from "src/components/common/AppLoadingAndErrorWrapper";
import EmailBuilderComm from "./EmailBuilderComm";
import { useNavigate, useParams } from "react-router";
import { SendCustomMessageSchema } from "src/schemas";
import { yupResolver } from "@hookform/resolvers/yup";

interface IAddCustomMessageProps {}

const AddCustomMessage: React.FC<IAddCustomMessageProps> = () => {
  // #region States
  const confirm = useBoolean();
  const { messageId } = useParams();
  const navigate = useNavigate();
  const editorShortRef: any = useRef();
  const { usePostApi, useGetListApi, useGetItemApi } = useApiServices();
  const [dataToSend, setDataToSend] = useState<any>();
  const [userSend, setUserSend] = useState<any>();
  // #endregion States

  const methods = useForm<any>({
    resolver: yupResolver(SendCustomMessageSchema),
    defaultValues: {
      subject: "",
      userType: null,
      userCategory: null,
      programIds: [],
      lang: [],
    },
  });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  // #region Services
  const { data: programsData } = useGetListApi<ResDataType["programs"]>({
    url: API_URLS.PROGRAMS,
  });

  const {
    data: islamicStatus,
    isLoading: isLanguagesLoading,
    error: languagesError,
  } = useGetListApi<IBaseStatusDto[]>({
    url: API_URLS.ISLAMIC_STATUS_TYPE,
  });

  const { data: supportedLanguages } = useGetListApi<ResDataType["dropDowns"]>({
    url: API_URLS.LANGUAGE_TYPE,
  });

  const {
    mutate: addCustomMessage,
    isLoading: isAdding,
    error: addError,
  } = usePostApi<any>({
    url: API_URLS.SendCustomMessage,
    onSuccess: () => {
      confirm.onFalse();
      methods.reset();
      navigate("/dashboard/custom-message");
    },
  });

  const {
    data: customMessageData,
    isLoading: isCustomMessageLoading,
    isRefetching,
  } = useGetItemApi<any>({
    url: API_URLS.CustomMessage,
    id: messageId!,
    onSuccess: (data: any) => {
      if (messageId) {
        methods.setValue(`subject`, data.subject);
        methods.setValue(`userType`, data.filter.userType);
        setUserSend(data.filter.userType);
        methods.setValue(`programIds`, data.filter.programIds);
        methods.setValue(`userCategory`, data.filter.userCategory);
        methods.setValue(`lang`, data.filter.lang);
      }
    },
  });

  const onSubmit = useCallback(
    async (dataFome: any) => {
      editorShortRef?.current.editor.exportHtml((data: any) => {
        const { design, html } = data;
        setDataToSend({
          ...dataFome,
          userType: userSend,
          emailMessage: html,
          emailDesignShort: design,
        });
      });
      confirm.onTrue();
    },
    [confirm]
  );

  return (
    <>
      <Box p={{ md: 8, xs: 3 }}>
        <AppLoadingAndErrorWrapper
          isLoading={isAdding || isCustomMessageLoading}
          errorMessage={addError}
          sx={{ height: "auto" }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography fontSize={28} fontWeight={500} align="center">
                  Send New Message
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <RHFTextField label="Subject Email" name="subject" />
              </Grid>
              <Grid item xs={6}>
                <Block label="User">
                  <RHFSelect
                    name="userType"
                    disabled={!!messageId}
                    onChange={(item) => {
                      setUserSend(item.target.value);
                      methods.setValue(`userType`, item.target.value);
                    }}
                  >
                    <MenuItem key={1} value="all">
                      All User
                    </MenuItem>
                    <MenuItem key={2} value="IN_PROGRAM">
                      User in program
                    </MenuItem>
                  </RHFSelect>
                </Block>
              </Grid>
              {userSend == "IN_PROGRAM" && (
                <Grid item xs={6}>
                  <Block label="Program Name">
                    <RHFMultiSelect
                      name="programIds"
                      disabled={!!messageId}
                      checkbox
                      options={(programsData?.items || [])?.map((program) => ({
                        label: program.name,
                        value: program.id,
                      }))}
                    />
                  </Block>
                </Grid>
              )}
              <Grid item xs={6}>
                <Block label="Islamic Status">
                  <RHFSelect name="userCategory" disabled={!!messageId}>
                    {(islamicStatus || [])?.map((option, index) => (
                      <MenuItem key={index} value={option?.value}>
                        {option?.label}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Block>
              </Grid>
              <Grid item xs={6}>
                <Block label="Choose user languages">
                  <RHFMultiSelect
                    disabled={!!messageId}
                    checkbox
                    name="lang"
                    options={supportedLanguages || []}
                  />
                </Block>
              </Grid>
              <Grid item xs={12}>
                <EmailBuilderComm
                  editorShortRef={editorShortRef}
                  customMessageData={customMessageData}
                />
              </Grid>

              {!messageId && (
                <Grid item xs={4}>
                  <AppLoadingButton label={"Send"} isLoading={isAdding} />
                </Grid>
              )}
            </Grid>
          </FormProvider>
        </AppLoadingAndErrorWrapper>
      </Box>

      {/* Confirm */}
      <ConfirmDialog
        open={confirm.value}
        onClose={(
          event: React.SyntheticEvent<Element, Event>,
          reason: string
        ) => {
          if (reason && reason === "backdropClick") {
            confirm.onFalse();
            // onClose();
          }
        }}
        content={`Are you sure you want to send this message?`}
        icon={<BorderColorOutlinedIcon fontSize="large" sx={{ mx: "auto" }} />}
        action={
          <>
            <AppButton
              fullWidth
              label="No"
              color="secondary"
              onClick={() => {
                confirm.onFalse();
              }}
            />
            <AppLoadingButton
              label="Yes"
              isLoading={isAdding}
              onClick={() => addCustomMessage(dataToSend!)}
            />
          </>
        }
      />
    </>
  );
};

export default AddCustomMessage;
